import React, { useState, useEffect, useRef, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAiSettings } from '../lib/aiSettingsContext';

// "Home" in the nav points to the daily snapshot ("Home" was renamed
// from "Dashboard" earlier — it's the user's personal home page). The
// brand logo (top-left) still points to `/` which is the public landing
// page, so the marketing/intro is always one click away.
const NAV_LINKS = [
  { to: '/dashboard', label: 'Home' },
  { to: '/chart', label: 'Chart' },
  { to: '/prashna', label: 'Prashna' },
  { to: '/graha', label: 'Graha' },
  { to: '/rashi', label: 'Rashi' },
  { to: '/nakshatra', label: 'Nakshatra' },
  { to: '/varga', label: 'Varga' },
  { to: '/big-picture', label: 'Big Picture' },
  { to: '/about', label: 'About' },
];

function Navbar({ savedNames = [], activeProfile = '', onProfileChange }) {
  const location = useLocation();
  const { open: openAiSettings } = useAiSettings();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopPickerOpen, setDesktopPickerOpen] = useState(false);
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);

  const desktopProfileRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const desktopDropdownId = useId();
  const mobileDropdownId = useId();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDesktopPickerOpen(false);
    setMobilePickerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 960) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!desktopPickerOpen && !mobilePickerOpen) return undefined;

    const onDocPointerDown = (e) => {
      const t = e.target;
      if (desktopPickerOpen && desktopProfileRef.current?.contains(t)) return;
      if (mobilePickerOpen && mobileProfileRef.current?.contains(t)) return;
      setDesktopPickerOpen(false);
      setMobilePickerOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setDesktopPickerOpen(false);
        setMobilePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [desktopPickerOpen, mobilePickerOpen]);

  const linkClass = (path) =>
    `nav-link ${location.pathname === path ? 'nav-link--active' : ''}`;

  const pickProfile = (name) => {
    onProfileChange(name);
    setDesktopPickerOpen(false);
    setMobilePickerOpen(false);
  };

  const renderProfileDropdown = (dropdownId, variant) => (
    <ul
      id={dropdownId}
      className={`navbar-profile-dropdown ${
        variant === 'mobile-header' ? 'navbar-profile-dropdown--mobile-header' : ''
      }`}
      role="listbox"
      aria-label="Saved charts"
    >
      {savedNames.map((name) => (
        <li key={name} role="presentation">
          <button
            type="button"
            role="option"
            aria-selected={name === activeProfile}
            className={`navbar-profile-option ${name === activeProfile ? 'navbar-profile-option--active' : ''}`}
            onClick={() => pickProfile(name)}
          >
            {name}
          </button>
        </li>
      ))}
      {activeProfile ? (
        <li role="presentation">
          <button
            type="button"
            className="navbar-profile-option navbar-profile-option--clear"
            onClick={() => pickProfile('')}
          >
            Clear selection
          </button>
        </li>
      ) : null}
    </ul>
  );

  return (
    <nav
      className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${menuOpen ? 'navbar--menu-open' : ''}`}
      aria-label="Main"
    >
      <div className="navbar-inner">
        <Link to="/" className="navbar-brand">
          <svg className="brand-yantra" viewBox="0 0 36 36" width="28" height="28" aria-hidden="true">
            <rect x="4" y="4" width="28" height="28" rx="1" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.4"/>
            <rect x="10" y="10" width="16" height="16" rx="0.5" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" transform="rotate(45 18 18)"/>
            <circle cx="18" cy="18" r="3" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.8"/>
            <circle cx="18" cy="18" r="1" fill="currentColor" opacity="0.9"/>
          </svg>
          <span className="brand-text">AntarDristi</span>
        </Link>

        <div className="navbar-cluster-desktop">
          {savedNames.length > 0 && (
            <div className="navbar-profile" ref={desktopProfileRef}>
              <button
                type="button"
                className="navbar-profile-trigger"
                onClick={() => setDesktopPickerOpen((o) => !o)}
                aria-expanded={desktopPickerOpen}
                aria-haspopup="listbox"
                aria-controls={desktopDropdownId}
              >
                <span className="navbar-profile-name" title={activeProfile || undefined}>
                  {activeProfile || 'Select chart'}
                </span>
                <span className="navbar-profile-chevron" aria-hidden />
              </button>
              {desktopPickerOpen && renderProfileDropdown(desktopDropdownId, 'desktop')}
            </div>
          )}
          <div className="navbar-links">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass(to)}>
                <span className="nav-link-label">{label}</span>
              </Link>
            ))}
          </div>
          <button
            type="button"
            className="navbar-gear"
            onClick={openAiSettings}
            aria-label="AI Settings"
            title="AI Settings — manage Gemini API keys"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </div>

        {savedNames.length > 0 && (
          <div className="navbar-profile navbar-profile-mobile-bar" ref={mobileProfileRef}>
            <button
              type="button"
              className="navbar-profile-trigger navbar-profile-trigger--mobile-header"
              onClick={() => setMobilePickerOpen((o) => !o)}
              aria-expanded={mobilePickerOpen}
              aria-haspopup="listbox"
              aria-controls={mobileDropdownId}
              aria-label={activeProfile ? `Current chart: ${activeProfile}. Change chart` : 'Select saved chart'}
            >
              <span className="navbar-profile-name" title={activeProfile || undefined}>
                {activeProfile || 'Select chart'}
              </span>
              <span className="navbar-profile-chevron" aria-hidden />
            </button>
            {mobilePickerOpen && renderProfileDropdown(mobileDropdownId, 'mobile-header')}
          </div>
        )}

        <button
          type="button"
          className="navbar-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="navbar-mobile-drawer"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="navbar-menu-bars" aria-hidden="true">
            <span className="navbar-menu-bar" />
            <span className="navbar-menu-bar" />
            <span className="navbar-menu-bar" />
          </span>
        </button>
      </div>

      {menuOpen && (
        <button
          type="button"
          className="navbar-backdrop"
          aria-label="Close menu"
          tabIndex={-1}
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div
        id="navbar-mobile-drawer"
        className="navbar-mobile-drawer"
        aria-hidden={!menuOpen}
      >
        <div className="navbar-mobile-links">
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={linkClass(to)}
              onClick={() => setMenuOpen(false)}
            >
              <span className="nav-link-label">{label}</span>
            </Link>
          ))}
          <button
            type="button"
            className="nav-link nav-link--ghost"
            onClick={() => { openAiSettings(); setMenuOpen(false); }}
          >
            <span className="nav-link-label">AI Settings</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
